// React Query hooks para módulo de facturación (quotes & invoices)
// Proporciona cache, dedup, refetch automático y mutaciones

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingService } from '@/services/billingService';
import type {
  ServiceQuote,
  ServiceInvoice,
  QuoteListResponse,
  QuoteFilters,
  QuoteCreateRequest,
  QuoteUpdateRequest,
  QuoteItemCreateRequest,
  QuoteItemUpdateRequest,
  InvoiceListResponse,
  InvoiceFilters,
  InvoiceCreateRequest,
  RecordPaymentRequest,
  LinkInvoiceRequest,
} from '@/types/billing';
import { toast } from 'sonner';

// ─── Query Keys ────────────────────────────────────────────

export const billingKeys = {
  // Quotes
  quotes: ['quotes'] as const,
  quoteLists: () => [...billingKeys.quotes, 'list'] as const,
  quoteList: (filters?: QuoteFilters) => [...billingKeys.quoteLists(), filters] as const,
  quoteDetails: () => [...billingKeys.quotes, 'detail'] as const,
  quoteDetail: (id: string) => [...billingKeys.quoteDetails(), id] as const,
  // Invoices
  invoices: ['invoices'] as const,
  invoiceLists: () => [...billingKeys.invoices, 'list'] as const,
  invoiceList: (filters?: InvoiceFilters) => [...billingKeys.invoiceLists(), filters] as const,
  invoiceDetails: () => [...billingKeys.invoices, 'detail'] as const,
  invoiceDetail: (id: string) => [...billingKeys.invoiceDetails(), id] as const,
  // Public
  publicQuote: (token: string) => ['public-quote', token] as const,
};

// ─── Quotes: Queries ──────────────────────────────────────

export function useQuotes(filters?: QuoteFilters) {
  return useQuery<QuoteListResponse>({
    queryKey: billingKeys.quoteList(filters),
    queryFn: () => billingService.getQuotes(filters),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useQuote(id: string | undefined) {
  return useQuery<ServiceQuote>({
    queryKey: billingKeys.quoteDetail(id!),
    queryFn: () => billingService.getQuote(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

// ─── Quotes: Mutations ───────────────────────────────────

export function useCreateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: QuoteCreateRequest) => billingService.createQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Presupuesto creado exitosamente');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al crear el presupuesto');
    },
  });
}

export function useUpdateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: QuoteUpdateRequest }) =>
      billingService.updateQuote(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Presupuesto actualizado');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al actualizar el presupuesto');
    },
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.deleteQuote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      queryClient.removeQueries({ queryKey: billingKeys.quoteDetail(id) });
      toast.success('Presupuesto eliminado');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al eliminar el presupuesto');
    },
  });
}

// ─── Quote Items: Mutations ──────────────────────────────

export function useAddQuoteItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, data }: { quoteId: string; data: QuoteItemCreateRequest }) =>
      billingService.addQuoteItem(quoteId, data),
    onSuccess: (_, { quoteId }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(quoteId) });
      toast.success('Línea agregada');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al agregar la línea');
    },
  });
}

export function useUpdateQuoteItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      quoteId,
      itemId,
      data,
    }: {
      quoteId: string;
      itemId: string;
      data: QuoteItemUpdateRequest;
    }) => billingService.updateQuoteItem(quoteId, itemId, data),
    onSuccess: (_, { quoteId }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(quoteId) });
      toast.success('Línea actualizada');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al actualizar la línea');
    },
  });
}

export function useDeleteQuoteItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, itemId }: { quoteId: string; itemId: string }) =>
      billingService.deleteQuoteItem(quoteId, itemId),
    onSuccess: (_, { quoteId }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(quoteId) });
      toast.success('Línea eliminada');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al eliminar la línea');
    },
  });
}

// ─── Quote Actions: Mutations ────────────────────────────

export function useSendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.sendQuote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Presupuesto enviado al cliente');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al enviar el presupuesto');
    },
  });
}

export function useAcceptQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.acceptQuote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Presupuesto aceptado');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al aceptar el presupuesto');
    },
  });
}

export function useRejectQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      billingService.rejectQuote(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Presupuesto rechazado');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al rechazar el presupuesto');
    },
  });
}

export function useCancelQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.cancelQuote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Presupuesto cancelado');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al cancelar el presupuesto');
    },
  });
}

export function useCreateRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.createRevision(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Nueva revisión creada');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al crear la revisión');
    },
  });
}

export function useInvoiceQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.invoiceQuote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      toast.success('Factura generada desde presupuesto');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al generar la factura');
    },
  });
}

// ─── Invoices: Queries ────────────────────────────────────

export function useInvoices(filters?: InvoiceFilters) {
  return useQuery<InvoiceListResponse>({
    queryKey: billingKeys.invoiceList(filters),
    queryFn: () => billingService.getInvoices(filters),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery<ServiceInvoice>({
    queryKey: billingKeys.invoiceDetail(id!),
    queryFn: () => billingService.getInvoice(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

// ─── Invoices: Mutations ──────────────────────────────────

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InvoiceCreateRequest) => billingService.createInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      toast.success('Factura creada exitosamente');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al crear la factura');
    },
  });
}

export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecordPaymentRequest }) =>
      billingService.recordPayment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      toast.success('Pago registrado');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al registrar el pago');
    },
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.voidInvoice(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      toast.success('Factura anulada');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al anular la factura');
    },
  });
}

export function useSendInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingService.sendInvoice(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(id) });
      toast.success('Factura enviada al cliente por email');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al enviar la factura');
    },
  });
}

export function useLinkInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LinkInvoiceRequest }) =>
      billingService.linkInvoice(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      toast.success('Factura vinculada a Stripe');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al vincular la factura');
    },
  });
}

// ─── Public Quote: Queries & Mutations ────────────────────

export function usePublicQuote(token: string | undefined) {
  return useQuery<ServiceQuote>({
    queryKey: billingKeys.publicQuote(token!),
    queryFn: () => billingService.getPublicQuote(token!),
    enabled: !!token,
    staleTime: 30 * 1000,
  });
}

export function useAcceptPublicQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => billingService.acceptPublicQuote(token),
    onSuccess: (_, token) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.publicQuote(token) });
      toast.success('Presupuesto aceptado. Gracias.');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al aceptar el presupuesto');
    },
  });
}

export function useRejectPublicQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ token, reason }: { token: string; reason?: string }) =>
      billingService.rejectPublicQuote(token, reason),
    onSuccess: (_, { token }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.publicQuote(token) });
      toast.success('Presupuesto rechazado');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail || 'Error al rechazar el presupuesto');
    },
  });
}
